if (!customElements.get('product-form')) {
  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[type="submit"]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // An error message only applies to the quantity it was shown for: clear it
        // when the quantity changes (the input can sit outside the form, linked
        // with form=; the sticky bar's stepper syncs into that same input).
        document.addEventListener('change', (event) => {
          if (event.target.name === 'quantity' && event.target.form === this.form) this.handleErrorMessage();
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        if (this.submitButton.getAttribute('aria-disabled') === 'true') return;

        this.handleErrorMessage();

        this.submitButton.setAttribute('aria-disabled', true);
        this.submitButton.classList.add('loading');
        this.querySelector('.loading__spinner').classList.remove('hidden');

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        let added = false;
        if (this.cart) {
          // Bundled section rendering: the add response carries every section the
          // cart drawer needs (incl. its cross-sell), so nothing is fetched again.
          formData.append(
            'sections',
            this.cart.getSectionIdsForAdd
              ? this.cart.getSectionIdsForAdd()
              : this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        fetch(`${routes.cart_add_url}`, config)
          .then((response) =>
            response
              .json()
              .catch(() => null)
              .then((body) => ({ ok: response.ok, status: response.status, body }))
          )
          .then(({ ok, status, body }) => {
            if (!ok || !body || body.status) {
              this.onAddFailed(status, formData, body);
              return;
            }
            const response = body;
            added = true;
            if (!this.cart) {
              window.location = window.routes.cart_url;
              return;
            }

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form',
              productVariantId: formData.get('id'),
              cartData: response,
            }).then(() => {
              CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
            });
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            // The item is in the cart but drawing it failed: refresh, no error message.
            if (added) {
              if (this.cart && typeof this.cart.refreshSections === 'function') this.cart.refreshSections();
              return;
            }
            this.onAddFailed(0, formData, null);
          })
          .finally(() => {
            this.submitButton.classList.remove('loading');
            this.submitButton.removeAttribute('aria-disabled');
            this.querySelector('.loading__spinner').classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      // A rejected (422), failed (network / 5xx) or malformed (400) add. Shopify's
      // own text is never shown: it can include the stock count ("Only N items
      // were added..."). The cart is refreshed once either way: a 422 can still
      // have added what was in stock, and a 400 doesn't mean nothing changed.
      onAddFailed(status, formData, body) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'product-form',
          productVariantId: formData.get('id'),
          errors: body && (body.errors || body.description),
          message: body && body.message,
        });
        const fallback = window.cartStrings && window.cartStrings.error;
        this.handleErrorMessage(
          (status === 422 ? this.dataset.errorRejected : this.dataset.errorFailed) || fallback
        );

        if (this.cart && typeof this.cart.refreshSections === 'function') {
          this.cart.refreshSections().then((sections) => {
            if (!sections) return;
            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'product-form-refresh',
              productVariantId: formData.get('id'),
              cartData: { sections },
            });
          });
        }
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
